/**
 * シナリオ設定画面「参考画像」アコーディオン内の画像生成モデル選択ポータルドロップダウン。
 *
 * シナリオ編集ページの「動画を生成」ドロップダウン (VideoModelDropdownPortal) /
 * シナリオ生成AIモデルドロップダウン (ScenarioModelDropdownPortal) と同じ共通部品
 * (ModelDropdownMenu) で描画し、見た目を揃える。
 * - OpenAI GPT Image 系 (2.5 Flare / 2.5 Sunburst のファミリーごと) / Google Gemini を
 *   アイコン付き見出しでグルーピング。GPT 系の見出しは通称 (標準 / 長時間) で、
 *   ツールチップに正式名称 (generation_models.model_family_name) を出す
 * - 各項目に、そのモデルで静止画 (フレーム/絵コンテ) を1枚生成する際の消費ポイントバッジを
 *   付ける。料率ソースはシーン編集画面の「フレーム再生成」等の pt バッジ (creditBadgeText /
 *   useCreditCalculations.imageCredits) と同一 — creditRatesData (credit_rate_config) を
 *   resolveImageResourceType(model, imageSize) で引く。imageSize は動画生成設定の解像度
 *   (videoResolution) の既定値ベース (resolveImageSizeFromResolution。'720'→1K、それ以外/未指定
 *   →2K) で、解像度セレクタは持たないため常に既定値扱い。料率が引けないモデルはバッジ非表示。
 * - forcedRecommendedModelCode に一致する行には is_recommended の値に関わらず常に推奨バッジを出す
 *   (Gemini 標準をシナリオフォームの既定にしたことに伴う表示専用の上書き。DB の is_recommended は
 *   静止画生成側 (MediaRequirementsAccordion) と共有のため書き換えない)
 * - Gemini ライト (gemini-3.1-flash-lite-image、表示名「スピード」) は 1K 固定モデル。
 *   1K 以外の解像度が選ばれているときはこの行を disabled にし、理由をホバーツールチップで示す
 *   (pt バッジ自体は resolveImageResourceType の 1K 固定特例により常に表示される)。
 */
import {
  ModelDropdownItem,
  ModelDropdownMenuShell,
  ModelDropdownSectionHeader,
  ModelDropdownSeparator,
} from '../common/ModelDropdownMenu';
import { RecommendedBadge } from '../../common/RecommendedBadge';
import { Tooltip } from '../../common/Tooltip';
import { resolveImageResourceType, resolveImageSizeFromResolution } from './hooks/useCreditCalculations';
import { GENERATION_MODEL_LITE } from '../state/useGenerationState';
import { groupGptImageModelsByFamily, isGptImageModelCode, stripGptPrefix } from '../utils/imageModelFamily';
import styles from './ScenarioGenerator.module.css';
import type { GenerationModel } from '../../../features/generation-models/api';
import type { CreditRatesResponse } from '../../../features/organizations/types';
import type { UsePortalDropdownReturn } from './hooks/usePortalDropdown';

interface ImageModelDropdownPortalProps {
  dropdown: UsePortalDropdownReturn;
  models: GenerationModel[];
  activeModelCode: string;
  onSelect: (modelCode: string) => void;
  forcedRecommendedModelCode?: string;
  /** pt バッジ算出用。未指定 (取得前) なら全行バッジ非表示で degrade する。 */
  creditRatesData?: CreditRatesResponse;
  /** 動画生成設定の解像度 ('1080'/'720')。未指定時は既定 (2K 相当) 扱い。 */
  videoResolution?: string;
}

interface ModelGroup {
  key: string;
  label: string;
  /** 見出しのツールチップ (通称表示時の正式名称)。無ければ非表示 */
  tooltip?: string | null;
  iconMask: string;
  list: GenerationModel[];
  labelFor: (m: GenerationModel) => string;
}

export function ImageModelDropdownPortal({
  dropdown,
  models,
  activeModelCode,
  onSelect,
  forcedRecommendedModelCode,
  creditRatesData,
  videoResolution,
}: ImageModelDropdownPortalProps) {
  if (!dropdown.openId || !dropdown.pos) return null;

  const imageSize = resolveImageSizeFromResolution(videoResolution);
  const lookupPts = (modelCode: string): number | null => {
    const resourceType = resolveImageResourceType(modelCode, imageSize);
    if (!resourceType || !creditRatesData?.rates) return null;
    const rate = creditRatesData.rates.find(
      (r) => r.service_type === 'image_generation' && r.resource_type === resourceType,
    );
    return rate?.credits_per_unit ?? null;
  };

  const gptFamilies = groupGptImageModelsByFamily(models);
  const geminiModels = models
    .filter((m) => !isGptImageModelCode(m.model_code))
    .sort((a, b) => a.sort_order - b.sort_order);

  const groups: ModelGroup[] = gptFamilies.map((family) => ({
    key: family.key,
    label: family.label,
    tooltip: family.tooltip,
    iconMask: '/openai_icon.svg',
    list: family.list,
    // display_label から "GPT" プレフィックスを落とし品質のみ見せる (GPT標準 → 標準)
    labelFor: (m) => stripGptPrefix(m.display_label),
  }));
  if (geminiModels.length > 0) {
    groups.push({
      key: 'gemini',
      label: 'Google Gemini',
      iconMask: '/gemini_icon_white.svg',
      list: geminiModels,
      labelFor: (m) => m.display_label,
    });
  }

  const items: React.ReactNode[] = [];
  groups.forEach((group, idx) => {
    if (idx > 0) items.push(<ModelDropdownSeparator key={`sep-${group.key}`} />);
    items.push(
      <ModelDropdownSectionHeader
        key={`hdr-${group.key}`}
        label={group.label}
        iconMask={group.iconMask}
        tooltip={group.tooltip}
      />,
    );
    group.list.forEach((m) => {
      const isActive = activeModelCode === m.model_code;
      const isRecommended = m.is_recommended || m.model_code === forcedRecommendedModelCode;
      const pts = lookupPts(m.model_code);
      // Gemini ライトは 1K 固定。1K 以外の解像度が選ばれている間は選べないようにする。
      const isLiteLocked = m.model_code === GENERATION_MODEL_LITE && imageSize !== '1K';
      const itemLabel = (
        <>
          {isRecommended && <RecommendedBadge active={isActive} />}
          <span>{group.labelFor(m)}</span>
        </>
      );
      const handleSelect = () => {
        onSelect(m.model_code);
        dropdown.close();
      };
      // ロック行のみ Tooltip (block/width100%な div ラッパー) で包む。それ以外は
      // ModelDropdownItem を直接積んで既存の width:100% レイアウトを崩さない。
      items.push(
        isLiteLocked ? (
          <Tooltip key={m.model_code} content="1K解像度専用" className={styles.modelDropdownItemTooltipWrap}>
            <ModelDropdownItem
              label={itemLabel}
              pts={pts !== null ? `${pts}pt` : undefined}
              active={isActive}
              disabled={isLiteLocked}
              onSelect={handleSelect}
            />
          </Tooltip>
        ) : (
          <ModelDropdownItem
            key={m.model_code}
            label={itemLabel}
            pts={pts !== null ? `${pts}pt` : undefined}
            active={isActive}
            onSelect={handleSelect}
          />
        ),
      );
    });
  });

  return (
    <ModelDropdownMenuShell
      menuRef={dropdown.menuRef}
      pos={dropdown.pos}
      emptyText="利用可能な画像生成モデルがありません"
    >
      {items}
    </ModelDropdownMenuShell>
  );
}
